/**
 * Attorney questions — agent-side creation and listing (slice 2, DARK).
 *
 * A created question lands on stage 'queued' and stops there. Nothing routes it, emails an
 * attorney, or answers it yet; the routing engine is slice 3.
 *
 * Authorization is server-side: both tables are deny-by-default and reachable only through
 * these SECURITY DEFINER RPCs, which require an ACTIVE profile in
 * admin/superadmin/manager/support_admin/support_agent. Agents see the questions they raised;
 * management sees all.
 *
 * See docs/DESIGN_ATTORNEY_QUESTION_ROUTING.md
 */

import { supabase } from "./supabase";

export type AttorneyTaskType =
  | "attorney_required"
  | "out_of_state_attorney_required"
  | "coverage_question";

export const ATTORNEY_TASK_TYPES: {
  value: AttorneyTaskType;
  label: string;
  help: string;
  states: "one" | "many" | "none";
}[] = [
  {
    value: "attorney_required",
    label: "Attorney required",
    help: "The member's own state, pre-selected from their record. Goes to every attorney for that state. For Texas, the member's ZIP picks north or south; if it cannot, you choose the Texas attorney.",
    states: "one",
  },
  {
    value: "out_of_state_attorney_required",
    label: "Out-of-state attorney required",
    help: "Tick every state or region involved. Each one's attorneys are asked separately.",
    states: "many",
  },
  {
    value: "coverage_question",
    label: "Coverage question",
    help: "Not routed to an attorney. Goes to the coverage questions mailbox.",
    states: "none",
  },
];

export interface AttorneyQuestionRow {
  support_task_id: string;
  task_number: number;
  task_type: AttorneyTaskType;
  stage: string;
  question_text: string;
  states: string[];
  member_name: string | null;
  membership_id: string | null;
  created_by_name: string | null;
  created_at: string;
  disposition: string | null;
  attorney_notes: string | null;
  /** Who each state went to (mig 898). Names only — agents get no attorney contact details. */
  assignments?: AttorneyAssignment[];
}

/**
 * One recipient of a filed question (mig 907): every attorney on each chosen jurisdiction,
 * plus the Coverage Questions mailbox CC. Attorneys by name only.
 */
export interface AttorneyAssignment {
  kind?: "attorney" | "cc" | "to";
  /** "OH", or a region code such as "TX-S" / "NC-E". NULL for the CC. */
  jurisdiction_code?: string | null;
  state_code?: string;
  attorney_name: string | null;
  tier?: "primary" | "backup" | null;
  sub_region?: string | null;
  needs_review?: boolean | null;
  /** Mailbox rows (cc / to) only: the address, or null when not configured yet. */
  cc_email?: string | null;
  /** Mig 1610: the agent chose this region because the member's ZIP could not place them. */
  region_chosen_by_agent?: boolean | null;
}

/**
 * Why a state cannot be routed automatically. Every one of these means ASSIGN BY HAND —
 * never "no attorney needed".
 */
export const UNROUTABLE_REASON_LABELS: Record<string, string> = {
  no_attorney_on_file: "no attorney on file",
  needs_county_map:
    "this state is split by region and the CRM has no map for it yet",
  needs_texas_zip:
    "Texas is split north/south by the member's ZIP, and this member has no Texas ZIP",
  needs_member_zip_in_state:
    "this state is split by region and the member's ZIP does not place them",
  region_required: "this state is split by region; pick the region",
  unknown_jurisdiction: "not a jurisdiction on the attorney record",
  region_codes_are_for_out_of_state:
    "regions are only chosen for out-of-state questions",
  no_attorney_for_region:
    "the attorney for the member's part of this state is switched off",
  member_not_in_state:
    "a region can only be chosen for the member's own state; use Out-of-state attorney required instead",
  region_set_by_member_zip:
    "the member's ZIP already decides the region, so a different region cannot be chosen",
  invalid_state_code: "not a valid state code",
};

export function unroutableLabel(reason: string | null | undefined): string {
  return (
    (reason && UNROUTABLE_REASON_LABELS[reason]) ||
    "no attorney can be assigned automatically"
  );
}

/** Operator-readable stage labels — the raw values are internal vocabulary. */
export const STAGE_LABELS: Record<string, string> = {
  submitted: "Submitted",
  queued: "Waiting to send",
  sent_to_attorney: "With attorney",
  answered: "Answered",
  agent_notified: "Answer ready for you",
  closed: "Closed",
  exception_review_required: "Wrong attorney — under review",
  training_review_required: "Not a legal question — under review",
};

function friendlyError(message: string): string {
  if (message.includes("not_authorized")) {
    return "You do not have permission to raise attorney questions.";
  }
  if (message.includes("no_active_coverage_for_state:")) {
    // Server format (mig 898): no_active_coverage_for_state:<ST>:<reason>
    const [state, reason] = (
      message.split("no_active_coverage_for_state:")[1] ?? ""
    ).split(":");
    return `${state?.slice(0, 2) || "That state"} cannot be routed automatically (${unroutableLabel(
      reason?.trim(),
    )}). Assign this question by hand.`;
  }
  if (message.includes("region_required:")) {
    const state = message.split("region_required:")[1]?.slice(0, 2);
    return `${state ?? "That state"} is split by region. Pick the region (for example ${state}-North or ${state}-South).`;
  }
  if (message.includes("unknown_jurisdiction:")) {
    return "One of the chosen regions is not on the attorney record.";
  }
  if (message.includes("region_set_by_member_zip:")) {
    // Server format (mig 1610): region_set_by_member_zip:<ST>:<the ZIP's region>
    const [, zipRegion] = (
      message.split("region_set_by_member_zip:")[1] ?? ""
    ).split(":");
    return `The member's ZIP places them in ${zipRegion?.trim() || "a region"}, so that region's attorney gets the question. Choose the state, not a different region.`;
  }
  if (message.includes("contact_is_former_member")) {
    return "This person is a former member: their membership has ended. Attorney questions can only be raised for current members.";
  }
  if (message.includes("region_codes_are_for_out_of_state")) {
    return "Regions are only chosen for out-of-state questions. For the member's own state, their ZIP decides.";
  }
  if (message.includes("contact_was_never_a_member")) {
    return "This person is not a member. Attorney questions can only be raised for current members.";
  }
  if (message.includes("too_many_states")) {
    return "Too many states selected.";
  }
  if (message.includes("attorney_question_routing_disabled")) {
    return "Attorney question routing is switched off in this environment.";
  }
  if (message.includes("exactly_one_state_required")) {
    return "Choose exactly one state for an Attorney Required question.";
  }
  if (message.includes("at_least_one_state_required")) {
    return "Choose at least one state.";
  }
  if (message.includes("coverage_question_takes_no_state")) {
    return "Coverage questions are not routed by state — clear the state selection.";
  }
  if (message.includes("question_required")) {
    return "Enter the member's question.";
  }
  if (message.includes("member_required")) {
    return "Choose the member this question is about.";
  }
  if (message.includes("invalid_task_type")) {
    return "Choose a question type.";
  }
  if (message.includes("invalid_state_code")) {
    return "That state code is not valid.";
  }
  return message;
}

export interface CreateAttorneyQuestionInput {
  contact_id: string;
  task_type: AttorneyTaskType;
  question_text: string;
  states: string[];
  priority?: string;
}

export async function createAttorneyQuestion(
  input: CreateAttorneyQuestionInput,
): Promise<{ support_task_id: string; task_number: number }> {
  const { data, error } = await supabase.rpc("create_attorney_question", {
    p_contact_id: input.contact_id,
    p_task_type: input.task_type,
    p_question_text: input.question_text,
    p_states: input.states,
    p_priority: input.priority ?? "medium",
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  // RETURNS TABLE surfaces as an array of one row.
  const row = Array.isArray(data) ? data[0] : data;
  return row as { support_task_id: string; task_number: number };
}

export async function listAttorneyQuestions(
  limit = 100,
): Promise<AttorneyQuestionRow[]> {
  const { data, error } = await supabase.rpc("list_attorney_questions", {
    p_limit: limit,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyQuestionRow[];
}

export interface AttorneyStateRouting {
  state_code: string;
  routable: boolean;
  /** Split state (Texas): the member's ZIP decides which attorney. */
  by_zip: boolean;
  reason: string | null;
}

/** Every state and DC: routable or not, and why not (mig 898). */
export async function listAttorneyStateRouting(): Promise<
  AttorneyStateRouting[]
> {
  const { data, error } = await supabase.rpc("list_attorney_state_routing");
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyStateRouting[];
}

/** One preview row: an attorney recipient, an unroutable choice, or the CC (mig 907). */
export interface AttorneyRoutePreview {
  recipient_kind: "attorney" | "cc" | "to";
  jurisdiction_code: string | null;
  state_code: string | null;
  routed: boolean;
  attorney_name: string | null;
  tier: "primary" | "backup" | null;
  sub_region: string | null;
  listed_position: number | null;
  needs_review: boolean | null;
  cc_email: string | null;
  reason: string | null;
}

/** Everyone the chosen states/regions would go to for this member — the same resolver create uses. */
export async function previewAttorneyRouting(
  contactId: string,
  states: string[],
  taskType: AttorneyTaskType,
): Promise<AttorneyRoutePreview[]> {
  const { data, error } = await supabase.rpc("preview_attorney_routing", {
    p_contact_id: contactId,
    p_states: states,
    p_task_type: taskType,
  });
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyRoutePreview[];
}

/**
 * Out-of-state checkboxes (mig 907). DERIVED FROM THE ATTORNEY RECORD: whole states, and split
 * states as their regions (TX-N/TX-S, NC-E/NC-W, …). Editing the record changes the list.
 */
export interface AttorneyJurisdictionChoice {
  code: string;
  state_code: string;
  sub_region: string | null;
  label: string;
  region_label: string | null;
  attorney_names: string[];
  needs_review: boolean;
}

export async function listAttorneyJurisdictionChoices(): Promise<
  AttorneyJurisdictionChoice[]
> {
  const { data, error } = await supabase.rpc(
    "list_attorney_jurisdiction_choices",
  );
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyJurisdictionChoice[];
}

export type MemberCategory = "current" | "former" | "never" | "unknown";

export interface AttorneyQuestionMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone_number: string | null;
  membership_id: string | null;
  member_status: string | null;
  state: string | null;
  postal_code: string | null;
  /** The server's verdict. Since mig 1610 only "current" may be the subject; former / never / unknown are refused. */
  member_category: MemberCategory;
}

/**
 * Member search for this form: the shared staff contact search (name, email, phone or member
 * number) plus the member verdict create_attorney_question will apply.
 */
export async function searchAttorneyQuestionMembers(
  query: string,
  limit = 10,
): Promise<AttorneyQuestionMember[]> {
  const { data, error } = await supabase.rpc(
    "search_attorney_question_members",
    { p_query: query, p_limit: limit },
  );
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return (data ?? []) as AttorneyQuestionMember[];
}

/**
 * States an agent may choose: only those with an active PRIMARY attorney. The server refuses
 * anything else, so offering the full 50 would just manufacture failed submissions.
 *
 * Deliberately NOT list_attorney_coverage — that one is management-only and returns attorney
 * names and emails. A support agent needs the state codes and nothing more.
 */
export async function listCoveredStates(): Promise<string[]> {
  const { data, error } = await supabase.rpc("list_covered_states");
  if (error) {
    throw new Error(friendlyError(error.message));
  }
  return ((data ?? []) as { state_code: string }[]).map((r) => r.state_code);
}
